/**
 * ModeProvider — die globale UI-Modus-Achse: **schlicht** (einfache App,
 * Default) vs. **voll** (volles Programm). Spiegelt `.mode-schlicht` /
 * `.mode-voll` aufs <html> (CSS-Hook für dichtere Darstellung im Vollmodus).
 *
 * Der effektive Modus hängt vom Bereich ab (siehe `@/features/ui-mode`):
 * Patienten und Fachkräfte (klinik/ambulant) sind fix 'schlicht', Admin
 * fix 'voll'. Kein Bereich kann umschalten (`canToggleMode` liefert immer
 * `false`) — es gibt bewusst nur noch eine Ansicht pro Bereich.
 *
 * Diese Achse vereint die früher verstreuten Mechanismen (dashboard_simple-
 * Weiche, `useDashboardMode`, `mode='simple'`, `ui_flow_mode`, `?view=classic`)
 * an einer Stelle. `useDashboardMode` ist nur noch ein Adapter hierauf.
 *
 * Muss unterhalb des `<AreaProvider>` gemountet werden (nutzt `useArea`).
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { useArea } from '@/contexts/AreaContext';
import {
  canToggleMode,
  readStoredUiMode,
  resolveMode,
  writeStoredUiMode,
  type UiMode,
} from '@/features/ui-mode';

interface Ctx {
  /** Effektiver Modus (gesperrte Bereiche ignorieren die Präferenz). */
  mode: UiMode;
  /** Setzt die Präferenz — No-op in gesperrten Bereichen (patient/admin). */
  setMode: (next: UiMode) => void;
  /** Darf der aktuelle Bereich umschalten? (UI blendet den Toggle sonst aus.) */
  canToggle: boolean;
}

const ModeContext = createContext<Ctx | null>(null);

function applyModeClass(mode: UiMode) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.classList.toggle('mode-schlicht', mode === 'schlicht');
  root.classList.toggle('mode-voll', mode === 'voll');
}

export function ModeProvider({ children }: { children: ReactNode }) {
  const area = useArea();
  const [stored, setStored] = useState<UiMode | null>(() => readStoredUiMode());

  const mode = resolveMode(area, stored);
  const canToggle = canToggleMode(area);

  useEffect(() => {
    applyModeClass(mode);
  }, [mode]);

  const setMode = useCallback(
    (next: UiMode) => {
      // Gesperrte Bereiche (patient/admin) ignorieren explizite Wechsel.
      if (!canToggleMode(area)) return;
      setStored(next);
      writeStoredUiMode(next);
    },
    [area],
  );

  const value = useMemo<Ctx>(() => ({ mode, setMode, canToggle }), [mode, setMode, canToggle]);
  return <ModeContext.Provider value={value}>{children}</ModeContext.Provider>;
}

export function useMode(): Ctx {
  const ctx = useContext(ModeContext);
  if (!ctx) throw new Error('useMode must be used inside <ModeProvider>');
  return ctx;
}
